import { ObjectSchema, string, object, mixed, boolean, array } from 'yup';
import { ProfileForm } from './types';
import { l10nEnService } from '@uplift/services';
import { SeeClientsEnum } from '@uplift/types';
import { getIsInPersonOption } from './SessionOptionsForm.component';
import {
  ENGLISH_MATCHES,
  ENGLISH_MATCHES_WITH_SPACE,
  NUMBERS_MATCHES,
  VALID_YEAR_MATCHES,
  ENGLISH_MATCHES_WITH_ENTER,
  URL_MATCH,
  INSIDE_TEXT_EMAIL_MATCH,
} from '@uplift/constants';
import { settingsAddressValidation } from '@uplift/ui-v2';
import { GENDER_SELF_DESCRIBE_ID } from './GenderForm.component';

import * as Yup from 'yup';
import { RegExpMatcher, englishDataset, englishRecommendedTransformers } from 'obscenity';

const englishDatasetBuild = englishDataset.build();

export const profanityMatcher = new RegExpMatcher({
  ...englishDatasetBuild,
  whitelistedTerms: [...englishDatasetBuild.whitelistedTerms, 'assessment'],
  ...englishRecommendedTransformers,
});


type ValidationHash<T extends Record<string, Yup.AnyObject>> = {
  [Key in keyof T]: Key extends keyof T ? ((l10n: typeof l10nEnService) => ObjectSchema<T[Key]>) : never;
};

const mixedSeeClientsSchema = mixed<SeeClientsEnum>().oneOf([ SeeClientsEnum.telehealth, SeeClientsEnum.inPerson, SeeClientsEnum.videoAndInPerson ]);


const customPracticeErrorMessages = {
  address1: l10nEnService.validations.client.practiceAddress.line1Empty,
  city: l10nEnService.validations.client.practiceAddress.cityEmpty,
  state: l10nEnService.validations.client.practiceAddress.state,
  zip: l10nEnService.validations.client.practiceAddress.zipEmpty,
};


const VALIDATION_PROVIDER_BIO = Yup.string()
  .max(700, l10nEnService.validations.lengthMatches(0, 700))
  .matches(ENGLISH_MATCHES_WITH_ENTER, l10nEnService.validations.couldContainEnglishWithSpace)
  .test(
    'weblinkNotAllowed',
    l10nEnService.validations.weblinksNotAllowedInProviderProfile,
    (value) => !URL_MATCH.test(value ?? '')
  )
  .test(
    'emailNotAllowed',
    l10nEnService.validations.emailNotAllowedInProviderProfile,
    (value) => !INSIDE_TEXT_EMAIL_MATCH.test(value ?? '')
  )
  .test(
    'profanityNotAllowed',
    l10nEnService.validations.profanityNotAllowedInProviderProfiles,
    (value) => !profanityMatcher.hasMatch(value ?? '')
  )
  .required(l10nEnService.validations.requiredMoreInformation);


export const profileValidationHash: ValidationHash<ProfileForm> = {
  preferredName: () => object({
    preferredName: string()
  }),
  pronouns: () => object({
    pronouns: string()
  }),
  gender: () => object({
    genders: array().of(string().required()).required(),
    otherGender: string().when('genders', {
      is: (value: string[]) => value.includes(GENDER_SELF_DESCRIBE_ID),
      then: s => s.required('Please enter self-described gender'),
    }),
  }),
  faith: () => object({
    faiths: array().of(string().required()).required()
  }),
  ethnicity: () => object({
    ethnicities: array().of(string().required()).required()
  }),
  languages: () => object({
    spokenLanguages: array().of(string().required()).required()
  }),
  bio: () => object({
    bio: VALIDATION_PROVIDER_BIO
  }),
  practiceAddress: (l10n) => object().shape({
    practiceName: string(),
    addresses: array().of(
      practiceAddressValidationSchema(l10n).concat(object({
        displayStreetAddress: boolean().nullable().required(l10n.validations.client.practiceAddress.displayStreetAddress),
        locationId: string().optional(),
      }))
    ).required(),
  }) as ObjectSchema<ProfileForm['practiceAddress']>,
  sessionOption: (l10n) => object({
    locations: array().of(object({
      locationId: string().required(),
      practiceId: string().required(),
      seeClientsType: mixedSeeClientsSchema.nullable().required(),
      handicapAccessible: boolean().nullable().when('seeClientsType', {
        is: (seeClientsType: SeeClientsEnum) => getIsInPersonOption(seeClientsType),
        then: schema => schema.required(l10n.validations.handicapAccessibleError),
      })
    }))
  }) as ObjectSchema<ProfileForm['sessionOption']>,
  supportedAgeGroups: (l10n) => object({
    supportedAgeGroups: array().of(string().required()).required().min(1, l10n.onboarding.errors.chooseYourAgeGroup)
  }),
  modalities: () => object({
    therapyTypes: array().of(string().required()).required().min(1, 'Select the types of therapy you practice')
  }),
  specialties: () => object({
    specialties: array().of(string().required()).required().min(3, 'Select at least 3 specialties')
  }),
  communityExpertise: () => object({
    faiths: array().of(string().required()).required(),
    ethnicities: array().of(string().required()).required(),
    general: array().of(string().required()).required(),
  }),
  treatmentApproach: () => object({
    treatmentApproaches: array().of(string().required()).required().min(1, 'Select at least 1 treatment approaches')
  }),
  therapeuticStyle: () => object({
    therapeuticStyles: array().of(string().required()).required(),
  }),
  education: (l10n) => object({
    degrees: array().of(object({
      id: string().optional(),
      school: string()
        .matches(ENGLISH_MATCHES, l10n.validations.couldContainEnglish)
        .max(64, l10n.validations.lengthMatches(1, 64))
        .required('Enter the school where you earned your degree'),
      fieldOfStudy: string()
        .matches(
          ENGLISH_MATCHES_WITH_SPACE,
          l10n.validations.couldContainEnglishWithSpace
        )
        .max(50, l10n.validations.lengthMatches(1, 50))
        .required('Enter your field of study'),
      degree: string().required('Choose your degree type'),
      yearGraduated: string()
        .matches(NUMBERS_MATCHES, l10n.validations.validYear)
        .matches(VALID_YEAR_MATCHES, l10n.validations.notRelevantDate)
        .min(4, l10n.validations.lengthEqual(4))
        .max(4, l10n.validations.lengthEqual(4))
        .required('Enter the year you graduated'),
    })).required()
  }) as ObjectSchema<ProfileForm['education']>,
};

export const degreeValidationSchema = (l10n: typeof l10nEnService) => object({
  school: string()
    .matches(ENGLISH_MATCHES, l10n.validations.couldContainEnglish)
    .max(64, l10n.validations.lengthMatches(1, 64))
    .required('Enter the school where you earned your degree'),
  fieldOfStudy: string()
    .matches(
      ENGLISH_MATCHES_WITH_SPACE,
      l10n.validations.couldContainEnglishWithSpace
    )
    .max(50, l10n.validations.lengthMatches(1, 50))
    .required('Enter your field of study'),
  degree: string().required('Choose your degree type'),
  yearGraduated: string()
    .matches(NUMBERS_MATCHES, l10n.validations.validYear)
    .matches(VALID_YEAR_MATCHES, l10n.validations.notRelevantDate)
    .min(4, l10n.validations.lengthEqual(4))
    .max(4, l10n.validations.lengthEqual(4))
    .required('Enter the year you graduated'),
});

export const practiceAddressValidationSchema = (l10n: typeof l10nEnService) => settingsAddressValidation(l10n, customPracticeErrorMessages);

export const specialtiesFueValidationSchema = () => object({
  specialties: array().of(string().required()).test('specialties-length-validation', 'Select at least 3 specialties', (value) => {

    if (value && value.length > 0) {
      return value.length >= 3;
    }

    return true;
  })
});
